import React, { useState } from 'react';
import {
  Box,
  Text,
  SimpleGrid,
  Flex,
  Button,
  HStack,
  Center,
  Icon,
  Portal,
  useToast
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useDispatch } from 'react-redux';
import { MemberCard } from '../../componants/Feed/SharedMemberComponents';
import MemberProfileCard from '../../componants/Feed/MemberProfileCard';
import BurrowSpinner from '../../componants/UI/BurrowSpinner';
import GiftCreditsModal from '../../components/Credits/GiftCreditsModal';
import { addFriend } from '../../Redux/Login/friendsActions';
import { openReportMessageModal } from '../../utils/communityModalEvents';

/**
 * Maps a backend search user object to the member shape expected by MemberCard.
 */
const toMember = (user) => ({
  ...user,
  burrowerId: user.burrowerId,
  userAvatar: user.userAvatar || user.profilePicture,
  membershipTier: user.membershipTier || (user.accountType === 'free' ? 'base' : user.accountType || 'base'),
  accountLevel: user.accountLevel || 1,
  respectCount: user.respectCount || 0,
  isOnline: user.isOnline || false,
});

const UserSearchResults = ({
  results,
  loading,
  totalResults,
  currentPage,
  totalPages,
  onPageChange,
  onMessageUser,
  hasSearched: hasSearchedProp = false
}) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [giftRecipientId, setGiftRecipientId] = useState(null);
  const dispatch = useDispatch();
  const toast = useToast();

  const hasSearched = hasSearchedProp;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      if (startPage > 2) pages.push('...');
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      if (endPage < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const handleCardClick = (member) => {
    setSelectedMember(prev => prev?.burrowerId === member.burrowerId ? null : member);
  };

  // Close profile card first, THEN open message modal (avoids z-index overlap)
  const handleMessage = (burrowerId) => {
    setSelectedMember(null);
    setTimeout(() => {
      onMessageUser(burrowerId);
    }, 100);
  };

  // Add to Kin via Redux addFriend action
  const handleAddToKin = async (burrowerId) => {
    const result = await dispatch(addFriend(burrowerId));
    if (result?.success) {
      toast({
        title: 'Kin added',
        description: `${burrowerId} has been added to your Kin.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      setSelectedMember(null);
    } else {
      toast({
        title: 'Error adding Kin',
        description: result?.error || 'Failed to add Kin. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Gift MU — close profile card, open GiftCreditsModal
  const handleGiftMU = (burrowerId) => {
    setGiftRecipientId(burrowerId);
    setSelectedMember(null);
    setTimeout(() => {
      setIsGiftModalOpen(true);
    }, 100);
  };

  // Report user — uses the existing feedback/ticket system via communityModalEvents
  const handleReport = (burrowerId) => {
    setSelectedMember(null);
    setTimeout(() => {
      openReportMessageModal({
        senderName: burrowerId,
        messageId: `user-${burrowerId}`
      });
    }, 100);
  };

  return (
    <Box>
      {/* Results count */}
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="xs" color="gray.500" fontWeight={500}>
          {loading ? 'Searching...' : hasSearched ? `${totalResults} result${totalResults !== 1 ? 's' : ''}` : ''}
        </Text>
      </Flex>

      {/* No results message */}
      {!loading && results.length === 0 && hasSearched && (
        <Box textAlign="center" p={6} borderRadius="lg" bg="transparent">
          <Text color="gray.400" fontSize="sm">No users found. Try a different search term.</Text>
        </Box>
      )}

      {/* Welcome message */}
      {!loading && !hasSearched && (
        <Box textAlign="center" py={8}>
          <Icon as={SearchIcon} w={8} h={8} color="gray.600" mb={3} />
          <Text fontSize="sm" fontWeight={500} color="gray.400">Search for users in the Burrow Community</Text>
          <Text color="gray.600" mt={1} fontSize="xs">Enter a username or bio keyword above</Text>
        </Box>
      )}

      {/* Loading spinner */}
      {loading && (
        <Center py={8}>
          <BurrowSpinner size="xl" />
        </Center>
      )}

      {/* Selected member profile card — Portal overlay (matches Feed pattern) */}
      {selectedMember && (
        <Portal>
          {/* Backdrop */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
            zIndex={1400}
            onClick={() => setSelectedMember(null)}
          />
          {/* Card container */}
          <Flex
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={1401}
            align="center"
            justify="center"
            pointerEvents="none"
          >
            <Box
              pointerEvents="auto"
              maxW="400px"
              w="90vw"
              maxH="90vh"
              overflowY="auto"
              css={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: '#4A5568', borderRadius: '4px' }
              }}
            >
              <MemberProfileCard
                member={selectedMember}
                onClose={() => setSelectedMember(null)}
                onMessage={handleMessage}
                onAddToKin={handleAddToKin}
                onGiftMU={handleGiftMU}
                onReport={handleReport}
              />
            </Box>
          </Flex>
        </Portal>
      )}

      {/* Results grid */}
      {!loading && hasSearched && results.length > 0 && (
        <SimpleGrid columns={{ base: 2, sm: 3, md: 3, lg: 4 }} spacing={3}>
          {results.map(user => {
            const member = toMember(user);
            return (
              <MemberCard
                key={user._id}
                member={member}
                onClick={handleCardClick}
              />
            );
          })}
        </SimpleGrid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="center" mt={5}>
          <HStack spacing={1}>
            <Button
              size="xs"
              variant="ghost"
              color="gray.400"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
              _hover={{ color: 'white' }}
            >
              Prev
            </Button>
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <Text key={`ellipsis-${index}`} color="gray.600" fontSize="xs">...</Text>
              ) : (
                <Button
                  key={`page-${page}`}
                  size="xs"
                  variant={currentPage === page ? 'solid' : 'ghost'}
                  bg={currentPage === page ? 'whiteAlpha.200' : 'transparent'}
                  color={currentPage === page ? '#F6E05E' : 'gray.500'}
                  _hover={{ bg: 'whiteAlpha.100' }}
                  onClick={() => onPageChange(page)}
                  minW="28px"
                >
                  {page}
                </Button>
              )
            ))}
            <Button
              size="xs"
              variant="ghost"
              color="gray.400"
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              _hover={{ color: 'white' }}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}

      {/* Gift MU Modal — uses existing 3-step confirmation flow */}
      <GiftCreditsModal
        isOpen={isGiftModalOpen}
        onClose={() => setIsGiftModalOpen(false)}
        recipientId={giftRecipientId}
      />
    </Box>
  );
};

export default UserSearchResults;
